import React, { useState } from 'react';
import styled from 'styled-components';
import { PauseCircle, X } from 'lucide-react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearSalesFlowSession } from '../utils/salesSession';
import { useTranslation } from 'react-i18next';

// Which order-flow steps show the Standby control (and the step we resume to).
const STEP_BY_PATH = {
  '/prescription': 'prescription',
  '/frame-selection': 'frame',
  '/measurement': 'measurement',
  '/payment': 'payment',
};

const Trigger = styled.button`
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 0.5rem 0.9rem; border-radius: 8px; cursor: pointer;
  background: #fff; color: #b45309; border: 1.5px solid #fdba74; font-weight: 600; font-size: 0.85rem;
  &:hover { background: #fff7ed; }
`;
const Modal = styled.div`position: fixed; inset: 0; background: rgba(15,23,42,0.5); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 1rem;`;
const Box = styled.div`background: #fff; border-radius: 14px; width: 100%; max-width: 420px; padding: 1.5rem; box-shadow: 0 20px 50px rgba(0,0,0,0.25);`;
const Title = styled.h3`margin: 0; font-size: 1.15rem; font-weight: 700; color: #1e293b; display: flex; align-items: center; gap: 0.5rem;`;
const Label = styled.label`display: block; font-size: 0.85rem; font-weight: 500; color: #475569; margin: 1rem 0 0.4rem;`;
const Presets = styled.div`display: flex; gap: 0.5rem; flex-wrap: wrap;`;
const Preset = styled.button`
  padding: 0.5rem 0.85rem; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 0.85rem;
  border: 1.5px solid ${p => p.active ? '#f59e0b' : '#e5e7eb'};
  background: ${p => p.active ? '#fffbeb' : '#fff'}; color: ${p => p.active ? '#b45309' : '#475569'};
`;
const Input = styled.input`width: 100%; padding: 0.6rem 0.75rem; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 0.95rem; &:focus{ outline: none; border-color: #f59e0b;}`;
const Note = styled.textarea`width: 100%; padding: 0.6rem 0.75rem; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 0.9rem; min-height: 60px; resize: vertical; &:focus{ outline: none; border-color: #f59e0b;}`;
const Err = styled.div`color: #b91c1c; font-size: 0.82rem; margin-top: 0.75rem;`;
const Actions = styled.div`display: flex; justify-content: flex-end; gap: 0.6rem; margin-top: 1.25rem;`;
const Cancel = styled.button`padding: 0.55rem 1rem; border-radius: 8px; border: 1px solid #e5e7eb; background: #fff; color: #475569; font-weight: 600; cursor: pointer;`;
const Confirm = styled.button`padding: 0.55rem 1.1rem; border-radius: 8px; border: none; background: linear-gradient(135deg,#f59e0b,#d97706); color: #fff; font-weight: 700; cursor: pointer; &:disabled{ opacity:.6; cursor: not-allowed;}`;

const PRESETS = [1, 2, 4, 24];

const StandbyButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const step = STEP_BY_PATH[location.pathname];
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState('2');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  if (!step) return null;

  const submit = async () => {
    setErr('');
    const h = Number(hours);
    if (!Number.isFinite(h) || h <= 0) { setErr('Enter a valid number of hours.'); return; }
    const prescriptionId = sessionStorage.getItem('prescriptionId');
    const orderId = sessionStorage.getItem('orderId');
    if (!prescriptionId && !orderId) { setErr('Nothing to park yet — enter the client’s details first so there’s something to save.'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/orders/standby', { prescriptionId, orderId, hours: h, step, notes: notes.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      clearSalesFlowSession();
      navigate('/visitor-entry/standby');
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not put this order on standby.');
      setSaving(false);
    }
  };

  return (
    <>
      <Trigger type="button" onClick={() => { setErr(''); setOpen(true); }} title={t('standby.putThisOrderOnStandby')}>
        <PauseCircle size={16} /> {t('standby.standby')}
      </Trigger>
      {open && (
        <Modal onClick={() => !saving && setOpen(false)}>
          <Box onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title><PauseCircle size={20} color="#d97706" /> {t('standby.putOrderOnStandby')}</Title>
              <button type="button" onClick={() => !saving && setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} color="#64748b" /></button>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.88rem', margin: '0.5rem 0 0' }}>
              {t('standby.savesThisInProgressOrder')} <b>{t('standby.standby')}</b> {t('standby.listItMovesToUnsuccessful')}
            </p>

            <Label>{t('standby.standbyForHowLong')}</Label>
            <Presets>
              {PRESETS.map((p) => (
                <Preset key={p} type="button" active={String(p) === String(hours)} onClick={() => setHours(String(p))}>
                  {p === 24 ? t('standby.x24H') : `${p} h`}
                </Preset>
              ))}
            </Presets>
            <Label>{t('standby.orCustomHours')}</Label>
            <Input type="number" min="0.5" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} />

            <Label>{t('standby.noteOptional')}</Label>
            <Note value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('standby.eGWaitingForThe')} />

            {err && <Err>{err}</Err>}
            <Actions>
              <Cancel type="button" onClick={() => setOpen(false)} disabled={saving}>{t('standby.cancel')}</Cancel>
              <Confirm type="button" onClick={submit} disabled={saving}>{saving ? t('standby.saving') : t('standby.putOnStandby')}</Confirm>
            </Actions>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default StandbyButton;
